import { useMemo, useState } from "react";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import type { CmmdProject } from "@cmmd-forge/contracts";

import type { SidebarThreadSummary } from "~/types";
import { sanitizePublicUiError } from "~/productSafeLabels";
import { cmmdApiClient } from "~/environments/cmmdApi";
import { Button } from "../ui/button";
import { toastManager } from "../ui/toast";
import {
  SidebarRemoveProjectDialog,
  type ProjectRemovalSummary,
} from "../sidebar/SidebarRemoveProjectDialog";
import type { RemovalStepLabel } from "../sidebar/removalStepLabels";
import {
  createRemovableForgeProject,
  removeSidebarProject,
} from "../sidebar/sidebarProjectRemoval";
import type { ForgeExecutionTarget } from "./forgeExecutionTarget";
import { resolveCmmdProjectId } from "./cmmdProjectId";

export interface CmmdProjectDeleteControlProps {
  readonly project: CmmdProject;
  readonly forgeTargets: readonly ForgeProjectRemovalTarget[];
  readonly threadSummaries: readonly SidebarThreadSummary[];
  readonly onForgeProjectDeleted: () => Promise<void>;
  readonly onRefresh: () => Promise<void>;
}

export interface ForgeProjectRemovalTarget extends ForgeExecutionTarget {
  readonly cwd: string;
}

function projectRemovalSummary(
  targets: readonly ForgeProjectRemovalTarget[],
  threads: readonly SidebarThreadSummary[],
): ProjectRemovalSummary {
  const targetKeys = new Set(
    targets.map((target) => `${target.environmentId}:${target.projectId}`),
  );
  const projectThreads = threads.filter((thread) =>
    targetKeys.has(`${thread.environmentId}:${thread.projectId}`),
  );
  return {
    checkoutCount: targets.length,
    threadCount: projectThreads.length,
    checkouts: targets.map((target) => ({
      cwd: target.cwd,
      label: target.cwd.split("/").findLast((segment) => segment.length > 0) ?? target.projectId,
    })),
  };
}

export function CmmdProjectDeleteControl(props: CmmdProjectDeleteControlProps) {
  const [open, setOpen] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [removalStepLabel, setRemovalStepLabel] = useState<RemovalStepLabel | null>(null);
  const removalSummary = useMemo(
    () => projectRemovalSummary(props.forgeTargets, props.threadSummaries),
    [props.forgeTargets, props.threadSummaries],
  );
  const cmmdProjectId = resolveCmmdProjectId(props.project.id);

  const setDialogOpen = (nextOpen: boolean) => {
    setOpen(nextOpen);
  };

  const removeProject = () => {
    if (props.forgeTargets.length === 0) return;
    setRemoving(true);
    setRemovalStepLabel(null);
    void removeSidebarProject(createRemovableForgeProject(props.forgeTargets), {
      onProgress: setRemovalStepLabel,
      // Deleting the CMMD project DOES end its Environment. Stated rather than
      // inherited from the default: every other caller now passes false, so an
      // unstated value here would read as an oversight.
      destroyRuntime: true,
    })
      // Always unlink, never cascade: the CMMD project outlives its Forge
      // project by design, so the link is what has to go, not the board.
      .then(() =>
        Promise.all(
          props.forgeTargets.map((target) =>
            cmmdApiClient.deleteProjectLink({
              environmentId: target.environmentId,
              projectId: target.projectId,
              cwd: target.cwd,
              cmmdProjectId,
            }),
          ),
        ),
      )
      .then(() => props.onForgeProjectDeleted())
      .then(() => {
        toastManager.add({
          type: "success",
          title: "Project deleted",
          description: "The CMMD project was kept.",
        });
        setDialogOpen(false);
      })
      .catch((error: unknown) => {
        void props.onRefresh();
        toastManager.add({
          type: "error",
          title: "Project deletion is incomplete",
          description: sanitizePublicUiError(error, "The project was not fully deleted."),
        });
      })
      .finally(() => {
        setRemoving(false);
        setRemovalStepLabel(null);
      });
  };

  return (
    <>
      <Button
        variant="destructive"
        size="sm"
        disabled={props.forgeTargets.length === 0 || removing}
        onClick={() => setOpen(true)}
      >
        Delete project
      </Button>
      <SidebarRemoveProjectDialog
        open={open}
        onOpenChange={setDialogOpen}
        projectName={props.project.shortName ?? props.project.title}
        removesCloudRuntime={props.forgeTargets.some(
          (target) => classifyEnvironmentId(target.environmentId).isCloud,
        )}
        isRemovingProject={removing}
        removalStepLabel={removalStepLabel}
        removalSummary={removalSummary}
        onConfirmRemove={removeProject}
      />
    </>
  );
}
